import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import NavigationFirstLayer from './NavigationFirstLayer.jsx';
import NavigationSecondLayer from './NavigationSecondLayer.jsx';
import History from '@core/scripts/components/history';
import MessageBroker from 'deprecated-shared-ui/components/message-broker';
import Topics from '@core/scripts/components/topics';
import Landmark from '@core/scripts/components/landmark-adapter';
import Messages from '@core/scripts/components/messages';
import {useCheckItemActive} from '../../hooks/use-check-item-active.js';
import {useMobileNavigationContext} from '../../context/mobile-navigation-context.jsx';
import {getMobileNavigationItems} from '../../../helper/sub-navigation-helper.js';
import {HeaderContext} from '@header/scripts/components/context/HeaderContext.jsx';

export default function Navigation({moveContainerToTop, moveContainerBelowHeader}) {
    const {navigationItems, texts} = useMobileNavigationContext();
    const {closeMobileNavi} = useContext(HeaderContext);

    const isItemActive = useCheckItemActive();
    const initialFirstLevelItem = useMemo(() => navigationItems.find((item) => isItemActive(item)), []);
    const initialFirstLevelId = initialFirstLevelItem?.id || navigationItems[0]?.id;
    const mobileNavigationItems = getMobileNavigationItems(initialFirstLevelItem || navigationItems[0]);

    const initialSecondLevelItem = useMemo(
        () => mobileNavigationItems.find((item) => isItemActive(item)),
        [],
    );
    const firstNonOverviewItem = mobileNavigationItems.find(item => !item.overviewItem);
    const initialSecondLevelId = initialSecondLevelItem?.id || firstNonOverviewItem?.id || mobileNavigationItems[0]?.id;

    const [activeIds, setActiveIds] = useState({
        firstLevel: initialFirstLevelId,
        secondLevel: initialSecondLevelId,
    });
    const [showFirstLayer, setShowFirstLayer] = useState(!initialSecondLevelItem);

    const firstLayer = useRef();
    const secondLayerRef = useRef(null);
    const isInitialFirstLayer = useRef(true);
    const sendUserSeesBottomLandmark = useRef(true);
    const banderoleParentRef = useRef(null);
    const scrollContainerRef = useRef(null);

    useEffect(() => {
        banderoleParentRef.current = document.body.querySelector('.header__banderole')?.parentElement;
        removeBanderole()

        return () => {

            addBanderole();
        };
    }, []);

    const removeBanderole = () => {
        if (banderoleParentRef.current) {
            banderoleParentRef.current.remove();
        }
    };

    const addBanderole = () => {
        if (banderoleParentRef.current && !document.body.contains(banderoleParentRef.current)) {
            const header = document.querySelector('header');
            if (header) {
                document.body.insertBefore(banderoleParentRef.current, header);
            }
        }
    };

    useEffect(() => {
        if (showFirstLayer) {
            moveContainerBelowHeader();
        } else {
            moveContainerToTop();
        }

        const messages = [
            MessageBroker.subscribe(`${Topics.HISTORY}.menu`, () => {
                closeMobileNavi();
            }),
            MessageBroker.subscribe(`${Topics.HISTORY}.menuChannel`, () => {
                if (isInitialFirstLayer.current) {
                    backToFirstLayer();
                } else {
                    closeMobileNavi();
                }
            }),
        ];

        if (firstLayer.current) {
            firstLayer.current.addEventListener('scroll', (event) => {
                const {scrollHeight, scrollTop, clientHeight} = event.target;
                if (Math.abs(scrollHeight - clientHeight - scrollTop) < 1) {
                    if (sendUserSeesBottomLandmark.current) {
                        sendUserSeesBottomLandmark.current = false;
                        const lmValue = {view: `pictureNaviLayer\\bottom`};
                        Landmark.sendActionLandmark(lmValue);
                    }
                } else {
                    sendUserSeesBottomLandmark.current = true;
                }
            });
            isInitialFirstLayer.current = showFirstLayer;
            MessageBroker.publish(Messages.NAVIGATION_ACCORDION.RENDERED, {firstLayer: showFirstLayer});
        }

        return () => {
            if (firstLayer.current) {
                firstLayer.current.removeEventListener('scroll', () => {});
            }
            messages.forEach((message) => MessageBroker.unsubscribe(message));
        };
    }, []);

    useEffect(() => {
        if (showFirstLayer) {
            History.remove('menuChannel');
        } else {
            History.push('menuChannel');
        }
    }, [showFirstLayer]);

    const selectFirstLevelItem = (id) => {
        const item = navigationItems.find(item => item.id === id);
        const mobileItems = getMobileNavigationItems(item);
        // make sure the second level id is always valid
        setActiveIds({
            firstLevel: id,
            secondLevel: mobileItems[0]?.id,
        });
    };

    const selectSecondLevelItem = (id) => {
        setActiveIds((prevState) => ({
            ...prevState,
            secondLevel: id,
        }));
        setShowFirstLayer(false);
        moveContainerToTop();
    };

    const backToFirstLayer = () => {
        setShowFirstLayer(true);
        moveContainerBelowHeader();
    };

    const slide = showFirstLayer ? '' : '-slide';
    const firstLevelItem = navigationItems.find(item => item.id === activeIds.firstLevel);
    const mobileNavigationItemsForSecondLevel = getMobileNavigationItems(firstLevelItem);

    return (
        <div className={'navigation__layers-wrapper'}>
            <div ref={firstLayer} className={'navigation__first-layer ' + slide} jsh-first-navi-layer="">
                <NavigationFirstLayer
                    activeFirstLevelId={activeIds.firstLevel}
                    setActiveFirstLevelId={selectFirstLevelItem}
                    selectSecondLevelItem={selectSecondLevelItem}
                    visible={showFirstLayer}
                />
            </div>
            <div className={'navigation__second-layer ' + slide} ref={secondLayerRef}>
                <NavigationSecondLayer
                    key={`${activeIds.firstLevel}-${activeIds.secondLevel}`}
                    backToFirstLayer={backToFirstLayer}
                    firstLevelTitle={firstLevelItem?.title}
                    firstLevelId={firstLevelItem?.id}
                    secondLevelItems={mobileNavigationItemsForSecondLevel}
                    selectSecondLevelId={activeIds.secondLevel}
                    visible={!showFirstLayer}
                    texts={texts}
                    secondLayerRef={secondLayerRef}
                />
            </div>
        </div>
    );
}